import { ERROR_CODES, type ErrorCode } from "@breakout/shared/constants"

export { ERROR_CODES, type ErrorCode }

const ERROR_CODE_VALUES = Object.values(ERROR_CODES) as readonly string[]

function isErrorCode(code: string): code is ErrorCode {
	return ERROR_CODE_VALUES.includes(code)
}

export function getErrorCode(error: unknown): ErrorCode | undefined {
	if (hasConvexErrorData(error)) {
		const data = error.data
		if (data && typeof data === "object" && "code" in data && typeof data.code === "string") {
			if (isErrorCode(data.code)) {
				return data.code
			}
		}
	}
	return undefined
}

export function isKnownErrorCode(error: unknown, code: ErrorCode): boolean {
	return getErrorCode(error) === code
}

export function getErrorMessage(error: unknown, fallback = "Something went wrong"): string {
	// Handle Convex errors with data payload (has 'data' property)
	if (hasConvexErrorData(error)) {
		const data = error.data
		if (typeof data === "string") {
			return data
		}
		if (data && typeof data === "object" && "message" in data && typeof data.message === "string") {
			return data.message
		}
	}

	// Handle standard Error objects
	if (error instanceof Error) {
		// Clean up the message - remove "Uncaught (in promise)" prefixes
		let message = error.message
			.replace(/^Uncaught\s+\(in promise\)\s*/i, "")
			.replace(/^Error:\s*/i, "")
			.trim()

		// Handle common error patterns
		if (
			message.toLowerCase().includes("user rejected") ||
			message.toLowerCase().includes("user denied")
		) {
			return "Request cancelled"
		}

		if (
			message.toLowerCase().includes("network") ||
			message.toLowerCase().includes("failed to fetch")
		) {
			return "Network error. Please check your connection."
		}

		// Truncate long messages
		if (message.length > 100) {
			message = `${message.slice(0, 97)}...`
		}

		return message || fallback
	}

	// Handle string errors
	if (typeof error === "string") {
		return error || fallback
	}

	// Handle objects with message property
	if (
		error &&
		typeof error === "object" &&
		"message" in error &&
		typeof error.message === "string"
	) {
		return error.message || fallback
	}

	return fallback
}

function hasConvexErrorData(error: unknown): error is { data: unknown } {
	return error !== null && typeof error === "object" && "data" in error
}
